import React, { lazy, Suspense } from "react";
import "@/App.css";
import company from "./data/company.json";
import { CompanyHero, CompanyDivisions, CompanyPrinciples, CompanyInsights, PrivateCapitalSolutionsPreview } from "./components/institutional/CompanyPathways";
import { HomeLoanDiscovery } from "./components/home-loans/HomeLoanDiscovery";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";

// Public Pages
import { InstitutionalNavbar } from "@/components/institutional/InstitutionalNavbar";
import { InstitutionalHero } from "@/components/institutional/InstitutionalHero";
import { InstitutionalFooter } from "@/components/institutional/InstitutionalFooter";
import { About } from "@/components/institutional/About";
import { Contact } from "@/components/institutional/Contact";
import { Pathways } from "@/components/institutional/Pathways";
import { PlatformWorkspace } from "@/components/institutional/PlatformWorkspace";
import { CapitalNetworkLoop } from "@/components/institutional/CapitalNetworkLoop";
import { SelectedTransactions } from "@/components/institutional/SelectedTransactions";
import { OpportunityControls } from "@/components/institutional/OpportunityControls";
import { InfrastructureTrust } from "@/components/institutional/InfrastructureTrust";
import { CapitalSolutionsPage as CapitalSolutionsContent } from "@/components/institutional/CapitalSolutionsPage";
import { ForBorrowersPage as ForBorrowersContent } from "@/components/institutional/ForBorrowersPage";
import { ForLendersPage as ForLendersContent } from "@/components/institutional/ForLendersPage";
import { Insights } from "@/components/institutional/Insights";
import { InsightArticle } from "@/components/institutional/InsightArticle";
import { SiteJsonLd } from "@/components/institutional/SiteJsonLd";

// Legal Pages
import { PrivacyPolicy, TermsOfService, Disclosures, PlatformTerms, Security, TrustCenter } from "@/components/pages";

// Portal Components
const PortalLogin = lazy(() => import("@/components/portal/PortalLogin").then(m => ({ default: m.PortalLogin })));
import { ProtectedRoute } from "@/components/portal/ProtectedRoute";
const PortalDashboard = lazy(() => import("@/components/portal/PortalDashboard").then(m => ({ default: m.PortalDashboard })));
const MyDeals = lazy(() => import("@/components/portal/MyDeals").then(m => ({ default: m.MyDeals })));
const DealRoom = lazy(() => import("@/components/portal/DealRoom").then(m => ({ default: m.DealRoom })));
const BidSubmission = lazy(() => import("@/components/portal/BidSubmission").then(m => ({ default: m.BidSubmission })));
const ForgotPassword = lazy(() => import("@/components/portal/ForgotPassword").then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/components/portal/ResetPassword").then(m => ({ default: m.ResetPassword })));

// Admin Portal Components
const AdminDeals = lazy(() => import("@/components/portal/admin/AdminDeals").then(m => ({ default: m.AdminDeals })));
const CreateDeal = lazy(() => import("@/components/portal/admin/CreateDeal").then(m => ({ default: m.CreateDeal })));
const DealDetail = lazy(() => import("@/components/portal/admin/DealDetail").then(m => ({ default: m.DealDetail })));
const Counterparties = lazy(() => import("@/components/portal/admin/Counterparties").then(m => ({ default: m.Counterparties })));
const CreateCounterparty = lazy(() => import("@/components/portal/admin/CreateCounterparty").then(m => ({ default: m.CreateCounterparty })));
const CounterpartyDetail = lazy(() => import("@/components/portal/admin/CounterpartyDetail").then(m => ({ default: m.CounterpartyDetail })));
const UserManagement = lazy(() => import("@/components/portal/admin/UserManagement").then(m => ({ default: m.UserManagement })));
const ActivityLogs = lazy(() => import("@/components/portal/admin/ActivityLogs").then(m => ({ default: m.ActivityLogs })));
const LenderRequests = lazy(() => import("@/components/portal/admin/LenderRequests").then(m => ({ default: m.LenderRequests })));
const DealsTrash = lazy(() => import("@/components/portal/admin/DealsTrash").then(m => ({ default: m.DealsTrash })));
const SystemHealth = lazy(() => import("@/components/portal/admin/SystemHealth").then(m => ({ default: m.SystemHealth })));
const SelectedTransactionsAdmin = lazy(() => import("@/components/portal/admin/SelectedTransactionsAdmin").then(m => ({ default: m.SelectedTransactionsAdmin })));
const BidComparison = lazy(() => import("@/components/portal/admin/BidComparison").then(m => ({ default: m.BidComparison })));
const CRMReports = lazy(() => import("@/components/portal/admin/CRMReports").then(m => ({ default: m.CRMReports })));
const Tasks = lazy(() => import("@/components/portal/admin/Tasks").then(m => ({ default: m.Tasks })));
const ImportCounterparties = lazy(() => import("@/components/portal/admin/ImportCounterparties").then(m => ({ default: m.ImportCounterparties })));
const ImportHistory = lazy(() => import("@/components/portal/admin/ImportHistory"));
const PendingCounterparties = lazy(() => import("@/components/portal/admin/PendingCounterparties").then(m => ({ default: m.PendingCounterparties })));
const ComplianceTools = lazy(() => import("@/components/portal/admin/ComplianceTools").then(m => ({ default: m.ComplianceTools })));
const IncidentRegister = lazy(() => import("@/components/portal/admin/IncidentRegister").then(m => ({ default: m.IncidentRegister })));
const IncidentDetail = lazy(() => import("@/components/portal/admin/IncidentDetail").then(m => ({ default: m.IncidentDetail })));
const SecuritySettings = lazy(() => import("@/components/portal/SecuritySettings").then(m => ({ default: m.SecuritySettings })));
const MyAgreements = lazy(() => import("@/components/portal/MyAgreements").then(m => ({ default: m.MyAgreements })));
const InviteActivation = lazy(() => import("@/components/portal/InviteActivation"));
const NcndaAcceptance = lazy(() => import("@/components/portal/NcndaAcceptance"));
const NcndaManager = lazy(() => import("@/components/portal/admin/NcndaManager"));
const InviteAnalytics = lazy(() => import("@/components/portal/admin/InviteAnalytics"));
const LenderResponseAnalytics = lazy(() => import("@/components/portal/admin/LenderResponseAnalytics"));
const OnboardingFlow = lazy(() => import("@/components/portal/OnboardingFlow"));
const ExternalCreateDeal = lazy(() => import("@/components/portal/ExternalCreateDeal"));
const LenderProfileEdit = lazy(() => import("@/components/portal/LenderProfileEdit"));
const PipelineDashboard = lazy(() => import("@/components/portal/admin/PipelineDashboard"));
const ExecutionCockpit = lazy(() => import("@/components/portal/admin/ExecutionCockpit").then(m => ({ default: m.ExecutionCockpit })));
const GovernancePanel = lazy(() => import("@/components/portal/admin/GovernancePanel").then(m => ({ default: m.GovernancePanel })));
const ProcurementPack = lazy(() => import("@/components/portal/admin/ProcurementPack").then(m => ({ default: m.ProcurementPack })));
import { PageSEO } from "@/components/PageSEO";

const HomeLoansPage = lazy(() => import("./components/home-loans/HomeLoansPage").then(m => ({ default: m.HomeLoansPage })));
const HomeLoansCreditGuide = lazy(() => import("./components/home-loans/HomeLoansPage").then(m => ({ default: m.HomeLoansCreditGuide })));
const HomeLoanEnquiries = lazy(() => import("./components/portal/admin/HomeLoanEnquiries").then(m => ({ default: m.HomeLoanEnquiries })));
const WorkspaceSelection = lazy(() => import("./components/portal/WorkspaceSwitcher").then(m => ({ default: m.WorkspaceSelection })));
const HomeLoanWorkspace = lazy(() => import("./components/home-loans/HomeLoanWorkspace").then(m => ({ default: m.HomeLoanWorkspace })));

// Homepage - Institutional Version
const HomePage = () => {
  return (
    <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
      <PageSEO
        title={company.title}
        description={company.description}
        path="/"
      />
      <InstitutionalNavbar />
      <main>
      <CompanyHero />
      <CompanyDivisions />
      <CompanyPrinciples />
      <HomeLoanDiscovery />
      <InstitutionalHero division />
      <PlatformWorkspace />
      <PrivateCapitalSolutionsPreview />
      <Pathways />
      <CapitalNetworkLoop />
      <SelectedTransactions />
      <OpportunityControls />
      <CompanyInsights />
      <Contact />
      <InfrastructureTrust />
      </main>
      <InstitutionalFooter />
    </div>
  );
};

// About Page
const AboutPage = () => (
  <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
    <PageSEO
      title={company.aboutTitle}
      description={company.aboutDescription}
      path="/about"
    />
    <SiteJsonLd page="about" />
    <InstitutionalNavbar />
    <main className="pt-20">
      <About />
    </main>
    <InfrastructureTrust />
    <InstitutionalFooter />
  </div>
);

// Capital Solutions Page
const CapitalSolutionsPage = () => (
  <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
    <PageSEO
      title="Private Credit & Property Finance Australia | GP Capital"
      description="Private capital across the full structure: senior debt, bridge finance, construction finance, mezzanine, preferred equity, corporate growth capital, restructuring and special situations."
      path="/capital-solutions"
    />
    <InstitutionalNavbar />
    <main className="pt-20">
      <section style={{ background: 'var(--gpc-paper)', borderBottom: '1px solid var(--gpc-line-soft)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8" style={{ padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 4rem)' }}>
          <p className="gpc-eyebrow" style={{ marginBottom: '14px' }}>Private Capital</p>
          <h1
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(2.25rem, 4.4vw, 3.4rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: 'var(--gpc-ink)',
              marginBottom: '14px',
            }}
          >
            Capital across the{' '}
            <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>full structure.</span>
          </h1>
          <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '1.075rem', color: 'var(--gpc-ink-soft)', maxWidth: '36rem', lineHeight: 1.6 }}>
            Private capital across the full structure — from senior debt to equity — for real estate finance, corporate growth, restructuring and special situations, sourced through our curated institutional network.
          </p>
        </div>
      </section>
      <CapitalSolutionsContent />
    </main>
    <InfrastructureTrust />
    <InstitutionalFooter />
  </div>
);

// For Borrowers Page
const ForBorrowersPage = () => (
  <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
    <PageSEO
      title="For Borrowers | Institutional Private Capital | GP Capital"
      description="Structured access to institutional private capital for real estate finance, business growth, restructuring, expansion and strategic capital — through a controlled, NDA-gated process."
      path="/for-borrowers"
    />
    <SiteJsonLd page="for-borrowers" />
    <InstitutionalNavbar />
    <main className="pt-20">
      <ForBorrowersContent />
    </main>
    <InfrastructureTrust />
    <InstitutionalFooter />
  </div>
);

// For Lenders Page
const ForLendersPage = () => (
  <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
    <PageSEO
      title="For Capital Partners | Private Credit Opportunities | GP Capital"
      description="Institutional origination through a controlled platform. Curated private credit, real estate and corporate finance opportunities matched to your mandate."
      path="/for-lenders"
    />
    <SiteJsonLd page="for-lenders" />
    <InstitutionalNavbar />
    <main className="pt-20">
      <ForLendersContent />
    </main>
    <InfrastructureTrust />
    <InstitutionalFooter />
  </div>
);

// Contact Page
const ContactPage = () => {
  return (
    <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
      <PageSEO
        title="Contact — GP Capital"
        description={company.contactDescription}
        path="/contact"
      />
      <SiteJsonLd page="contact" />
      <InstitutionalNavbar />
      <main className="pt-20">
        {/* Hero */}
        <section style={{ background: 'var(--gpc-paper)', borderBottom: '1px solid var(--gpc-line-soft)' }}>
          <div className="max-w-7xl mx-auto px-6" style={{ padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 4rem)' }}>
            <p className="gpc-eyebrow" style={{ marginBottom: '14px' }}>Contact</p>
            <h1
              style={{
                fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
                fontWeight: 400,
                fontSize: 'clamp(2.25rem, 4.4vw, 3.4rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.015em',
                color: 'var(--gpc-ink)',
                marginBottom: '14px',
              }}
            >
              Talk to GP Capital.
            </h1>
            <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '1.075rem', color: 'var(--gpc-ink-soft)', maxWidth: '36rem', lineHeight: 1.6 }}>
              Choose Home Loans, Private Capital, capital-partner access or a general business enquiry. Each pathway has its own appropriate process.
            </p>
          </div>
        </section>
        <Contact />
      </main>
      <InfrastructureTrust />
      <InstitutionalFooter />
    </div>
  );
};

import { ErrorBoundary } from "@/components/ErrorBoundary";

const ToolsHub = lazy(() => import("./components/tools/FinanceTools").then(m => ({ default: m.ToolsHub })));
const CalculatorPage = lazy(() => import("./components/tools/FinanceTools").then(m => ({ default: m.CalculatorPage })));
import { BookAppointment } from "./components/pages/BookAppointment";
import { NotFound } from "./components/pages/NotFound";

function App({ Router = BrowserRouter, routerProps = {} }) {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Router {...routerProps}>
          <Suspense fallback={<p role="status" className="pt-24 text-center">Loading page…</p>}><Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/capital-solutions" element={<CapitalSolutionsPage />} />
            <Route path="/for-borrowers" element={<ForBorrowersPage />} />
            <Route path="/for-lenders" element={<ForLendersPage />} />
            {/* Canonical redirect: /for-lenders-family-offices → /for-lenders */}
            <Route path="/for-capital-partners" element={<Navigate to="/for-lenders" replace />} />
            <Route path="/for-lenders-family-offices" element={<Navigate to="/for-lenders" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/book-appointment" element={<BookAppointment />} />
            <Route path="/home-loans" element={<Suspense fallback={<p role="status" className="pt-24 text-center">Loading…</p>}><HomeLoansPage /></Suspense>} />
            <Route path="/home-loans/credit-guide" element={<Suspense fallback={<p role="status" className="pt-24 text-center">Loading…</p>}><HomeLoansCreditGuide /></Suspense>} />
            <Route path="/home-loans/portal" element={<HomeLoanWorkspace />} />
            <Route path="/portal/workspaces" element={<WorkspaceSelection />} />

            {/* Insights — Phase 100A */}
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightArticle />} />

            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/disclosures" element={<Disclosures />} />
            <Route path="/platform-terms" element={<PlatformTerms />} />
            <Route path="/security" element={<Security />} />
            <Route path="/trust" element={<TrustCenter />} />
            
            {/* Portal Auth */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/forgot-password" element={<ForgotPassword />} />
            <Route path="/portal/reset-password" element={<ResetPassword />} />
            
            {/* Invite, NCNDA & Onboarding (public / semi-public) */}
            <Route path="/invite/:token" element={<InviteActivation />} />
            <Route path="/portal/ncnda" element={<NcndaAcceptance />} />
            <Route path="/portal/onboarding" element={<OnboardingFlow />} />
            
            {/* Protected Portal Routes (all authenticated users) */}
            <Route element={<ProtectedRoute />}>
              <Route path="/portal/dashboard" element={<PortalDashboard />} />
              <Route path="/portal/deals" element={<MyDeals />} />
              <Route path="/portal/deals/new" element={<ExternalCreateDeal />} />
              <Route path="/portal/deals/:dealId" element={<DealRoom />} />
              <Route path="/portal/institution/profile" element={<LenderProfileEdit />} />
              <Route path="/portal/deals/:dealId/bid" element={<BidSubmission />} />
              <Route path="/portal/security" element={<SecuritySettings />} />
              <Route path="/portal/my-agreements" element={<MyAgreements />} />
            </Route>
            
            {/* Admin Portal Routes */}
            <Route element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'internal_team', 'read_only']} />}>
              <Route path="/portal/admin/deals" element={<AdminDeals />} />
              <Route path="/portal/admin/deals/trash" element={<DealsTrash />} />
              <Route path="/portal/admin/system" element={<SystemHealth />} />
              <Route path="/portal/admin/selected-transactions" element={<SelectedTransactionsAdmin />} />
              <Route path="/portal/admin/pipeline" element={<PipelineDashboard />} />
              <Route path="/portal/admin/cockpit" element={<ExecutionCockpit />} />
              <Route path="/portal/admin/governance" element={<GovernancePanel />} />
              <Route path="/portal/admin/deals/new" element={<CreateDeal />} />
              <Route path="/portal/admin/deals/:dealId" element={<DealDetail />} />
              <Route path="/portal/admin/deals/:dealId/edit" element={<CreateDeal />} />
              <Route path="/portal/admin/counterparties" element={<Counterparties />} />
              <Route path="/portal/admin/counterparties/pending" element={<PendingCounterparties />} />
              <Route path="/portal/admin/counterparties/import" element={<ImportCounterparties />} />
              <Route path="/portal/admin/import-history" element={<ImportHistory />} />
              <Route path="/portal/admin/counterparties/new" element={<CreateCounterparty />} />
              <Route path="/portal/admin/counterparties/:cpId" element={<CounterpartyDetail />} />
              <Route path="/portal/admin/counterparties/:cpId/edit" element={<CreateCounterparty />} />
              <Route path="/portal/admin/home-loan-enquiries" element={<Suspense fallback={<p role="status">Loading enquiries…</p>}><HomeLoanEnquiries /></Suspense>} />
              <Route path="/portal/admin/users" element={<UserManagement />} />
              <Route path="/portal/admin/activity" element={<ActivityLogs />} />
              <Route path="/portal/admin/lender-requests" element={<LenderRequests />} />
              <Route path="/portal/admin/reports" element={<CRMReports />} />
              <Route path="/portal/admin/tasks" element={<Tasks />} />
              <Route path="/portal/admin/bids/:dealId" element={<BidComparison />} />
              <Route path="/portal/admin/compliance" element={<ComplianceTools />} />
              <Route path="/portal/admin/ncnda" element={<NcndaManager />} />
              <Route path="/portal/admin/invite-analytics" element={<InviteAnalytics />} />
              <Route path="/portal/admin/lender-analytics" element={<LenderResponseAnalytics />} />
              <Route path="/portal/admin/incidents" element={<IncidentRegister />} />
              <Route path="/portal/admin/incidents/:incidentId" element={<IncidentDetail />} />
              <Route path="/portal/admin/procurement-pack" element={<ProcurementPack />} />
            </Route>
          <Route path="/tools" element={<ToolsHub />} /><Route path="/tools/:slug" element={<CalculatorPage />} /><Route path="*" element={<NotFound />} /></Routes></Suspense>
        </Router>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
